import { useCallback, useEffect, useMemo, useState } from "react";

import * as m from "../../../core/model/model";
import { useLazyApi } from "../../../hooks/useApi/useApi";
import * as proxy from "../../../services/api/proxy";

/**
 * Represents the current state of a Svea BankID signing flow.
 *
 * The `isTerminal` property indicates whether the process has reached its final state.
 * When `isTerminal` is `true`, no further state changes will occur and the caller can
 * safely act on the outcome.
 *
 * Non-terminal states:
 * - `Initializing`         â€“ The start request is in flight.
 * - `PendingStart`         â€“ BankID session started; waiting for the user to open the BankID app.
 *                            A `qrCode` may be present for QR-based flows.
 * - `AwaitingAuthorization`â€“ The user has opened BankID and must approve the signing request.
 * - `Polling`              â€“ Actively polling the backend for a status update.
 * - `Cancelling`           â€“ A cancellation has been requested; still waiting for confirmation.
 * - `Successful`           â€“ BankID approved; completing the signing session on the backend.
 *
 * Terminal states:
 * - `Completed`            â€“ Signing succeeded. `statusCode` and `content` contain the backend response.
 * - `Cancelled`            â€“ The process was cancelled (either by the caller or by the user in BankID).
 * - `Failed`               â€“ BankID reported a failure. `reason` describes the cause.
 * - `Error`                â€“ An unexpected error occurred. `message` may contain details.
 */
export type SignState =
	| { type: "Initializing"; isTerminal: false }
	| { type: "PendingStart"; isTerminal: false; qrCode?: string }
	| { type: "AwaitingAuthorization"; isTerminal: false }
	| { type: "Polling"; isTerminal: false }
	| { type: "Cancelling"; isTerminal: false }
	| { type: "Successful"; isTerminal: false }
	| { type: "Completed"; isTerminal: true; statusCode: number; content: null | string }
	| { type: "Cancelled"; isTerminal: true }
	| { type: "Failed"; isTerminal: true; reason: string; message?: string }
	| { type: "Error"; isTerminal: true; message?: string };

export interface NewSveaBankIdSignFlowResult {
	/** The current state of the signing flow. Poll this to react to progress. */
	signState: SignState;
	/**
	 * Requests cancellation of the ongoing signing process.
	 * The flow may not stop immediately â€” the state will transition through `Cancelling`
	 * and eventually settle on `Cancelled`. Safe to call at any point before a terminal state.
	 */
	cancel: () => void;
	/** Internal diagnostic data intended for debugging. Do not rely on this in production logic. */
	debugInfo: Record<string, unknown>;
	/**
	 * The BankID autostart token returned by the backend after starting the flow.
	 * Available once the flow has progressed past `Initializing`. Use this to deep-link
	 * into the native BankID app (e.g. via an `bankid://` URI on mobile).
	 */
	bankIdToken?: string;
}

/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
type InternalState =
	| "PendingStart"
	| "Initializing"
	| "Polling - fetching status"
	| "Polling - has interaction status"
	| "Polling - has non-interaction status"
	| "Initialized"
	| "Successful"
	| "Completed"
	| "Cancelling"
	| "Cancelled"
	| "Failed to fetch status"
	| string;
/* eslint-enable @typescript-eslint/no-redundant-type-constituents */

/**
 * Drives a complete Svea BankID signing flow for a given signing token.
 *
 * **Lifecycle:** The hook starts the signing process automatically on mount â€” no
 * explicit trigger is required. From that point it polls the backend and progresses
 * through a sequence of states, reflected in the returned `signState`. The caller should
 * re-render on state changes and act once a terminal state is reached
 * (`signState.isTerminal === true`).
 *
 * **Cancellation:** Call `cancel()` at any time to abort the process. The hook will send
 * a cancel request to the backend and continue polling until BankID confirms the
 * cancellation, at which point `signState` will be `{ type: "Cancelled", isTerminal: true }`.
 *
 * **Terminal states:** Once `isTerminal` is `true` the hook will no longer update.
 * - `Completed` â€“ Signing succeeded; use `statusCode` and `content` to handle the response.
 * - `Cancelled` â€“ The flow was aborted.
 * - `Failed`    â€“ BankID reported a failure; inspect `reason` for the cause.
 * - `Error`     â€“ An unexpected error occurred.
 *
 * @param signingToken â€“ The token identifying the signing operation to authorise, obtained
 *                       from the backend before mounting this hook.
 *
 * @example
 * const { signState, cancel } = useNewSveaBankIdSignFlow(signingToken);
 * if (signState.isTerminal) { ... }
 * if (signState.type === "Completed") { doSomethingWith(signState.content); }
 */
export const useNewSveaBankIdSignFlow = (signingToken: string): NewSveaBankIdSignFlowResult => {
	const [start, startRequest, resetStart] = useLazyApi(proxy.Bank.SveaAuth2Controller.StartSign.Post);
	const [getStatus, getStatusRequest, resetGetStatus] = useLazyApi(proxy.Bank.SveaAuth2Controller.StatusSign.Get);
	const [complete, completeRequest, resetComplete] = useLazyApi(proxy.Bank.SveaAuth2Controller.CompleteSign.Post);
	const [cancel, cancelRequest, resetCancel] = useLazyApi(proxy.Bank.SveaAuth2Controller.CancelSign.Post);

	const [isCancelling, setIsCancelling] = useState(false);

	const bankIdToken = useMemo(() => {
		return startRequest.data?.Data?.BankIdToken;
	}, [startRequest.data?.Data?.BankIdToken]);

	const startIsCompleted = useMemo(() => {
		return !startRequest.isLoading && !!startRequest.data;
	}, [startRequest.isLoading, startRequest.data]);

	const completeIsCompleted = useMemo(() => {
		return !completeRequest.isLoading && !!completeRequest.data;
	}, [completeRequest.isLoading, completeRequest.data]);

	const state_pendingStart = useMemo(
		() => !isCancelling && !startRequest.isLoading && !startRequest.data,
		[isCancelling, startRequest.isLoading, startRequest.data],
	);
	const state_initializing = useMemo(
		() => !isCancelling && startRequest.isLoading,
		[isCancelling, startRequest.isLoading],
	);
	const state_initialized = useMemo(
		() => !isCancelling && startIsCompleted && !getStatusRequest.isLoading && !getStatusRequest.data,
		[isCancelling, getStatusRequest.data, getStatusRequest.isLoading, startIsCompleted],
	);
	const state_polling_fetchingStatus = useMemo(
		() => !isCancelling && startIsCompleted && !completeIsCompleted && getStatusRequest.isLoading,
		[isCancelling, getStatusRequest.isLoading, startIsCompleted, completeIsCompleted],
	);
	const state_polling_hasInteractionStatus = useMemo(
		() =>
			!isCancelling &&
			startIsCompleted &&
			!completeIsCompleted &&
			!getStatusRequest.isLoading &&
			(getStatusRequest.data?.Data?.Status ===
				m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.AwaitingAuthorization ||
				getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.PendingStart),
		[
			isCancelling,
			getStatusRequest.data?.Data?.Status,
			getStatusRequest.isLoading,
			startIsCompleted,
			completeIsCompleted,
		],
	);
	const state_polling_hasNonInteractionStatus = useMemo(
		() =>
			!isCancelling &&
			startIsCompleted &&
			!completeIsCompleted &&
			!getStatusRequest.isLoading &&
			!!getStatusRequest.data &&
			(getStatusRequest.data?.Data?.Status ===
				m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_BankIdTokenAlreadyUsed ||
				getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_ExpiredTransaction ||
				getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_StartFailed ||
				getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_UnknownReason ||
				getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Error),
		[isCancelling, startIsCompleted, completeIsCompleted, getStatusRequest.data, getStatusRequest.isLoading],
	);
	const state_successful = useMemo(
		() =>
			!isCancelling &&
			startIsCompleted &&
			!completeIsCompleted &&
			!getStatusRequest.isLoading &&
			getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Successful,
		[
			isCancelling,
			getStatusRequest.data?.Data?.Status,
			getStatusRequest.isLoading,
			startIsCompleted,
			completeIsCompleted,
		],
	);
	const state_getStatusError = useMemo(
		() =>
			!isCancelling &&
			startIsCompleted &&
			!completeIsCompleted &&
			!getStatusRequest.isLoading &&
			getStatusRequest.error,
		[isCancelling, getStatusRequest.error, getStatusRequest.isLoading, startIsCompleted, completeIsCompleted],
	);
	const state_completed = useMemo(() => !isCancelling && completeIsCompleted, [isCancelling, completeIsCompleted]);

	const isInTerminalState = useMemo(
		() =>
			(!completeRequest.isLoading && !!completeRequest.data) ||
			(!getStatusRequest.isLoading &&
				!!getStatusRequest.data &&
				(getStatusRequest.data?.Data?.Status ===
					m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_BankIdTokenAlreadyUsed ||
					getStatusRequest.data?.Data?.Status ===
						m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_ExpiredTransaction ||
					getStatusRequest.data?.Data?.Status ===
						m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_StartFailed ||
					getStatusRequest.data?.Data?.Status ===
						m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_UserCancelled ||
					getStatusRequest.data?.Data?.Status ===
						m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_UnknownReason ||
					getStatusRequest.data?.Data?.Status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Error)),
		[completeRequest.isLoading, completeRequest.data, getStatusRequest.isLoading, getStatusRequest.data],
	);
	const state_cancelling = useMemo(() => isCancelling && !isInTerminalState, [isCancelling, isInTerminalState]);

	const state_cancelled = useMemo(
		() =>
			startIsCompleted &&
			!completeIsCompleted &&
			!getStatusRequest.isLoading &&
			!!getStatusRequest.data &&
			getStatusRequest.data?.Data?.Status ===
				m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_UserCancelled,
		[startIsCompleted, completeIsCompleted, getStatusRequest.data, getStatusRequest.isLoading],
	);

	const internalState: InternalState = useMemo(() => {
		const activeStates = [
			{ name: "PendingStart", value: state_pendingStart },
			{ name: "Initializing", value: state_initializing },
			{ name: "Polling - fetching status", value: state_polling_fetchingStatus },
			{ name: "Polling - has interaction status", value: state_polling_hasInteractionStatus },
			{ name: "Polling - has non-interaction status", value: state_polling_hasNonInteractionStatus },
			{ name: "Initialized", value: state_initialized },
			{ name: "Successful", value: state_successful },
			{ name: "Completed", value: state_completed },
			{ name: "Failed to fetch status", value: state_getStatusError },
			{ name: "Cancelling", value: state_cancelling },
			{ name: "Cancelled", value: state_cancelled },
		];

		const trueStates = activeStates.filter(s => s.value);
		if (trueStates.length === 0) {
			return "Undefined";
		}
		if (trueStates.length > 1) {
			return `Multiple - ${trueStates.map(s => s.name).join(", ")}`;
		}
		return trueStates[0].name;
	}, [
		state_pendingStart,
		state_initializing,
		state_polling_fetchingStatus,
		state_polling_hasInteractionStatus,
		state_polling_hasNonInteractionStatus,
		state_initialized,
		state_successful,
		state_completed,
		state_getStatusError,
		state_cancelling,
		state_cancelled,
	]);

	const startWithToken = useCallback(() => {
		start({ SigningToken: signingToken });
	}, [start, signingToken]);

	useEffect(() => {
		resetGetStatus();
		resetComplete();
		resetCancel();
		startWithToken();
	}, [resetComplete, resetGetStatus, resetStart, resetCancel, startWithToken]);

	useEffect(() => {
		if (internalState === "Initializing") {
		} else if (internalState == "Initialized") {
			getStatus(bankIdToken ?? "");
		} else if (internalState == "Polling - has interaction status") {
			getStatus(bankIdToken ?? "");
		} else if (internalState === "Polling - has non-interaction status") {
			if (bankIdToken) {
				cancel({ BankIdToken: bankIdToken });
			}
		} else if (internalState === "Successful") {
			complete({ BankIdToken: bankIdToken ?? "" });
		}
	}, [bankIdToken, cancel, complete, getStatus, internalState]);

	const signStatus = useMemo(() => {
		if (getStatusRequest.data) {
			return getStatusRequest.data;
		}
		if (startRequest.data) {
			return startRequest.data;
		}
		return null;
	}, [getStatusRequest.data, startRequest.data]);

	const signState: SignState = useMemo(() => {
		const status = signStatus?.Data?.Status;

		if (internalState === "Cancelling") {
			return { type: "Cancelling", isTerminal: false };
		}
		if (internalState === "Cancelled") {
			return { type: "Cancelled", isTerminal: true };
		}
		if (internalState === "Initializing") {
			return { type: "Initializing", isTerminal: false };
		}
		if (internalState === "Successful") {
			return { type: "Successful", isTerminal: false };
		}
		if (internalState === "Completed") {
			return {
				type: "Completed",
				isTerminal: true,
				statusCode: completeRequest?.data?.Data?.StatusCode ?? 0,
				content: completeRequest?.data?.Data?.Content ?? null,
			};
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.PendingStart) {
			return { type: "PendingStart", isTerminal: false, qrCode: signStatus?.Data?.QrCode ?? undefined };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.AwaitingAuthorization) {
			return { type: "AwaitingAuthorization", isTerminal: false };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Successful) {
			return { type: "Successful", isTerminal: false };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_BankIdTokenAlreadyUsed) {
			return { type: "Failed", isTerminal: true, reason: "BankIdTokenAlreadyUsed" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_ExpiredTransaction) {
			return { type: "Failed", isTerminal: true, reason: "ExpiredTransaction" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_StartFailed) {
			return { type: "Failed", isTerminal: true, reason: "StartFailed" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_UserCancelled) {
			return { type: "Failed", isTerminal: true, reason: "UserCancelled" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Failed_UnknownReason) {
			return { type: "Failed", isTerminal: true, reason: "UnknownReason" };
		}
		if (status === m.Bokio.Bank.Web.Controllers.SignStatusResultStatusEnum.Error) {
			return { type: "Error", isTerminal: true, message: signStatus?.Data?.Message ?? undefined };
		}

		if (internalState === "Polling - fetching status" || internalState === "Polling - has interaction status") {
			return { type: "Polling", isTerminal: false };
		}

		return { type: "Initializing", isTerminal: false };
	}, [
		signStatus?.Data?.Message,
		signStatus?.Data?.QrCode,
		signStatus?.Data?.Status,
		completeRequest?.data?.Data,
		internalState,
	]);

	const handleCancel = useCallback(() => {
		if (bankIdToken) {
			cancel({ BankIdToken: bankIdToken });
		}
		setIsCancelling(true);
	}, [bankIdToken, cancel]);

	const debugInfo: Record<string, unknown> = {
		"Internal State": internalState,
		"Sign State": signState.type,
		"Signing Token": signingToken,
		"BankId Token": bankIdToken,
		"Start Request": startRequest,
		"Status Request": getStatusRequest,
		"Complete Request": completeRequest,
		"Cancel Request": cancelRequest,
		"Sign Status": signStatus,
	};

	return { signState, cancel: handleCancel, debugInfo, bankIdToken };
};
